import React, {useCallback, useMemo, useRef} from 'react';
import {
    Box,
    Button,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C82_PAGE, C82_SEED_ROWS} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C82RiskActionPlanViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const ROW_FIELDS = ['ref', 'risk', 'rmm', 'response', 'outcome'] as const;

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    py: 0.75,
    px: 0.5,
    lineHeight: 1.25,
    whiteSpace: 'pre-line' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.5,
    px: 0.5,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

function CellInput({
    value,
    onChange,
    multiline,
    align = 'left',
    rows = 2,
}: {
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    align?: 'left' | 'center';
    rows?: number;
}) {
    if (multiline) {
        return (
            <Box
                component="textarea"
                value={value}
                rows={rows}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                sx={{...nativeBaseSx, resize: 'vertical', textAlign: align}}
            />
        );
    }
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, textAlign: align}}
        />
    );
}

function parseRowIds(raw?: string): string[] {
    if (!raw || !String(raw).trim()) return [];
    return String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

function discoverLegacyRowIds(values: Record<string, string>): string[] {
    const ids = new Set<string>();
    for (const seed of C82_SEED_ROWS) ids.add(seed.id);
    for (const key of Object.keys(values)) {
        const m = /^(r\d+|extra\d+|row_\d+)\./.exec(key);
        if (m) ids.add(m[1]);
    }
    const seedIds: string[] = C82_SEED_ROWS.map((r) => r.id);
    const rest = [...ids].filter((id) => !seedIds.includes(id)).sort();
    return [...seedIds.filter((id) => ids.has(id)), ...rest];
}

function nextRowId(existing: string[]): string {
    let n = 1;
    const set = new Set(existing);
    while (set.has(`row_${n}`)) n += 1;
    return `row_${n}`;
}

function nextRef(existingRefs: string[]): string {
    const nums = existingRefs
        .map((r) => Number.parseInt(String(r).trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0);
    const max = nums.length ? Math.max(...nums) : 0;
    return String(max + 1);
}

/** Excel C8.2 — Financial Statement Risk Action Plan (editable + add/remove rows). */
export default function C82RiskActionPlanView({header, values, onChange}: C82RiskActionPlanViewProps) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const set = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    const rowIds = useMemo(() => {
        const explicit = parseRowIds(values.rowIds);
        if (explicit.length) return explicit;
        return discoverLegacyRowIds(values);
    }, [values]);

    const f = (id: string, field: (typeof ROW_FIELDS)[number]) => values[`${id}.${field}`] ?? '';

    const handleAdd = () => {
        const id = nextRowId(rowIds);
        const ref = nextRef(rowIds.map((rid) => f(rid, 'ref')));
        const nextIds = [...rowIds, id];
        set('rowIds', nextIds.join(','));
        set(`${id}.ref`, ref);
        set(`${id}.risk`, '');
        set(`${id}.rmm`, '');
        set(`${id}.response`, '');
        set(`${id}.outcome`, '');
    };

    const handleDelete = (id: string) => {
        if (rowIds.length <= 1) return;
        set('rowIds', rowIds.filter((x) => x !== id).join(','));
        for (const field of ROW_FIELDS) {
            set(`${id}.${field}`, '');
        }
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader header={header} variant="a13" refCode="C8.2" values={values} onChange={set}/>
            <Typography variant="subtitle1" fontWeight={700} sx={{textAlign: 'center', mb: 1.5, mt: 0.5}}>
                {C82_PAGE.title}
            </Typography>
            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small" sx={{minWidth: 1000, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col style={{width: 56}}/>
                        <col/>
                        <col style={{width: 120}}/>
                        <col style={{width: 220}}/>
                        <col style={{width: 110}}/>
                        <col style={{width: 48}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} align="center">Ref</TableCell>
                            <TableCell sx={headSx}>{C82_PAGE.riskHeader}</TableCell>
                            <TableCell sx={headSx} align="center">{C82_PAGE.rmmHeader}</TableCell>
                            <TableCell sx={headSx}>Response</TableCell>
                            <TableCell sx={headSx} align="center">Outcome{'\n'}Ref</TableCell>
                            <TableCell sx={headSx}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rowIds.map((id) => (
                            <TableRow key={id}>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'ref')}
                                        onChange={(v) => set(`${id}.ref`, v)}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'risk')}
                                        onChange={(v) => set(`${id}.risk`, v)}
                                        multiline
                                        rows={2}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'rmm')}
                                        onChange={(v) => set(`${id}.rmm`, v)}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'response')}
                                        onChange={(v) => set(`${id}.response`, v)}
                                        multiline
                                        rows={2}
                                    />
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <CellInput
                                        value={f(id, 'outcome')}
                                        onChange={(v) => set(`${id}.outcome`, v)}
                                        align="center"
                                    />
                                </TableCell>
                                <TableCell sx={{...bodySx, px: 0}} align="center">
                                    <IconButton
                                        size="small"
                                        aria-label="delete row"
                                        onClick={() => handleDelete(id)}
                                        disabled={rowIds.length <= 1}
                                        sx={{color: 'text.secondary'}}
                                    >
                                        <DeleteOutlineIcon fontSize="small"/>
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1.5, gap: 2}}>
                <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.4, whiteSpace: 'pre-line'}}>
                    {C82_PAGE.note}
                </Typography>
                <Button size="small" startIcon={<AddIcon/>} onClick={handleAdd} variant="outlined" sx={{flexShrink: 0}}>
                    Add row
                </Button>
            </Box>
        </Box>
    );
}
